import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { Injectable } from '@angular/core';
import {
    datGrey,
    DatiqFontFamily,
    datiqFontFamilyCaption,
} from '@dat/datiq-components-pkg/utils';
import {
    isNumber,
    noop
} from 'lodash';
import {
    DatiqGraphAxisNumberTypes,
    IDatiqGraph,
    IDatiqGraphAxis,
    IDatiqGraphAxisMarker,
} from '../models';
import { DatiqAmcharts5AxisUtilsService } from './datiq-amcharts-5-axis-utils.service';
import {
    datiqAmCharts5NumberFormats,
    datiqAmcharts5SetColor
} from './models';

@Injectable({
    providedIn: 'root',
})
export class DatiqAmcharts5AxisNumberService {
    constructor(private _utils: DatiqAmcharts5AxisUtilsService) {}

    createXAxis(
        root: am5.Root,
        chart: am5xy.XYChart,
        axis: IDatiqGraphAxis,
        settings: IDatiqGraph
    ): am5xy.ValueAxis<am5xy.AxisRendererX> {
        const rangeAxisSettings: Partial<
            am5xy.IValueAxisSettings<am5xy.AxisRendererX>
        > = this.setUpXAxisRangeSettings(axis);
        const renderer: am5xy.AxisRendererX = this._utils.setXAxisRenderer(
            root,
            axis
        );
        const axisX: am5xy.ValueAxis<am5xy.AxisRendererX> = chart.xAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer,
                ...rangeAxisSettings,
            })
        );

        axisX.set(
            'tooltip',
            am5.Tooltip.new(root, {
                themeTags: ['axis'],
                forceHidden: true,
            })
        );
        this._utils.setUpXAxisRenderer(renderer, settings).setUpXAxis(axisX);

        return this.setGridLabelFormatting(renderer).buildAxisX(axisX);
    }

    buildAxisX(
        axisX: am5xy.ValueAxis<am5xy.AxisRendererX>
    ): am5xy.ValueAxis<am5xy.AxisRendererX> {
        return axisX;
    }

    createYAxis(
        root: am5.Root,
        chart: am5xy.XYChart,
        axis: IDatiqGraphAxis,
        settings: IDatiqGraph
    ): am5xy.ValueAxis<am5xy.AxisRendererY> {
        const renderer: am5xy.AxisRendererY = this._utils.setYAxisRenderer(
            root,
            axis
        );
        const rangeAxisSettings: Partial<
            am5xy.IValueAxisSettings<am5xy.AxisRendererY>
        > = this.setUpAxisRangeSettings(axis);
        const axisY: am5xy.ValueAxis<am5xy.AxisRendererY> = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer,
                numberFormat: this.setNumberFormat(axis),
                ...rangeAxisSettings,
            })
        );

        this._utils
            .setUpYAxisRenderer(renderer)
            .setUpYAxis(axisY)
            .setUpYAxisLabel(axisY, axis, root);

        return this.setGridLabelFormatting(renderer)
            .addMarkers(axisY, axis, root)
            .buildAxisY(axisY);
    }
    addMarkers(
        axisY: am5xy.ValueAxis<am5xy.AxisRendererY>,
        axisData: IDatiqGraphAxis,
        root: am5.Root
    ): this {
        axisData?.markers
            ?.filter((marker: IDatiqGraphAxisMarker) =>
                this.isValidMarker(marker)
            )
            ?.forEach((marker: IDatiqGraphAxisMarker) => {
                this.addMarker(axisY, marker, root);
            });

        return this;
    }

    private isValidMarker(marker: IDatiqGraphAxisMarker): unknown {
        return isNumber(marker.max) && isNumber(marker.min);
    }

    private addMarker(
        axisY: am5xy.ValueAxis<am5xy.AxisRendererY>,
        marker: IDatiqGraphAxisMarker,
        root: am5.Root
    ): this {
        let range = this.createMarkerRange(axisY, marker);

        this.addMarkerFill(range, marker)
            .addMarkerStroke(range, marker)
            .addMarkerLabel(marker, range, root);

        return this;
    }
    addMarkerStroke(
        range: am5.DataItem<am5xy.IValueAxisDataItem>,
        marker: IDatiqGraphAxisMarker
    ): this {
        this.useMarkerLine(marker)
            ? range.get('grid').setAll({
                  visible: false,
              })
            : noop();
        this.useMarkerLine(marker)
            ? range.get('axisFill').setAll({
                  stroke: datiqAmcharts5SetColor(marker.color),
                  strokeDasharray: 0,
                  strokeDashoffset: 0,
                  strokeOpacity: 1,
                  layer: 0,
              })
            : noop();
        return this;
    }

    private useMarkerLine(marker: IDatiqGraphAxisMarker) {
        return marker.max === marker.min;
    }

    private createMarkerRange(
        axisY: am5xy.ValueAxis<am5xy.AxisRendererY>,
        marker: IDatiqGraphAxisMarker
    ) {
        let rangeDataItem = axisY.makeDataItem({
            value: marker.min,
            endValue: marker.max,
        });

        return axisY.createAxisRange(rangeDataItem);
    }

    private addMarkerFill(
        range: am5.DataItem<am5xy.IValueAxisDataItem>,
        marker: IDatiqGraphAxisMarker
    ) {
        range.get('axisFill').setAll({
            fill: datiqAmcharts5SetColor(marker.color),
            fillOpacity: 0.8,
            visible: true,
        });

        return this;
    }

    private addMarkerLabel(
        marker: IDatiqGraphAxisMarker,
        range: am5.DataItem<am5xy.IValueAxisDataItem>,
        root: am5.Root
    ) {
        !!marker?.label?.length
            ? range.get('label').setAll({
                  text: marker.label.toUpperCase(),
                  fontSize: datiqFontFamilyCaption.fontSize,
                  fontWeight: datiqFontFamilyCaption.fontWeight,
                  fontFamily: DatiqFontFamily,
                  paddingTop: this.useMarkerLine(marker)
                      ? +datiqFontFamilyCaption.fontSize + 8
                      : 0,
                  paddingRight:
                      -0.75 *
                      marker.label.length *
                      +datiqFontFamilyCaption.fontSize,
                  fill: datiqAmcharts5SetColor(datGrey['500']),
                  location: 0.4,
                  layer: 5,
              })
            : noop();

        return this;
    }

    setUpAxisRangeSettings(
        axis: IDatiqGraphAxis
    ): Partial<am5xy.IValueAxisSettings<am5xy.AxisRendererY>> {
        if (!this.shouldSetYAxisRange(axis)) return {};

        return {
            min: axis.min as number,
            max: axis.max as number,
            strictMinMax: true,
        };
    }

    shouldSetYAxisRange(axis: IDatiqGraphAxis): boolean {
        return isNumber(axis.min) && isNumber(axis.max);
    }

    setNumberFormat(axis: IDatiqGraphAxis): string {
        return datiqAmCharts5NumberFormats[axis.numberAxisType] ?? '';
    }
    isPercent(axis: IDatiqGraphAxis): boolean {
        return axis.numberAxisType === DatiqGraphAxisNumberTypes.Percent;
    }

    isCurrency(axis: IDatiqGraphAxis) {
        return axis.numberAxisType === DatiqGraphAxisNumberTypes.Currency;
    }
    setGridLabelFormatting(renderer: am5xy.AxisRenderer): this {
        this._utils.setDefaultLabelStyles(renderer);

        return this;
    }

    buildAxisY(
        axisY: am5xy.ValueAxis<am5xy.AxisRendererY>
    ): am5xy.ValueAxis<am5xy.AxisRendererY> {
        return axisY;
    }

    setUpXAxisRangeSettings(
        axes: IDatiqGraphAxis
    ): Partial<am5xy.IValueAxisSettings<am5xy.AxisRendererX>> {
        if (!axes?.strictMinMax) return {};

        return {
            strictMinMax: true,
        };
    }
}
